/**
 * mock — sample feeds per widget, so the studio preview renders a REAL page
 * before any user has adopted the widget. Same shape as the host payload, so
 * `dataMode: 'mock'` and `dataMode: 'user'` run the identical interpreter.
 */
import { myWardrobeMock } from './myWardrobe';
import { isoDay } from '../viewir/derives';

export const MOCK_FEEDS = {
  'my-wardrobe': myWardrobeMock,
  // The local demo page renders the neweyes batch against the wardrobe rows —
  // it ships no feed of its own because it is never published.
  'neweyes-demo': myWardrobeMock,
};

const EMPTY_FEED = { entries: [], settings: {}, pending: { adds: [], updates: [] } };

export function mockFeed(widgetId) {
  return MOCK_FEEDS[widgetId] || EMPTY_FEED;
}

/**
 * Page args a non-entry page needs to be meaningful in the studio (a detail
 * page with no `item_id` has nothing to show). Picks the first plausible row
 * from the widget's own mock feed.
 */
export function mockPageArgs(widgetId, pageId) {
  const feed = mockFeed(widgetId);
  if (pageId === 'photo') {
    const first = feed.entries.find((e) => e.payload?.photo_url);
    return {
      session_id: first?.source_session_id || null,
      grid_id: first?.source_session_id || null,
      photo_url: first?.payload?.photo_url || null,
    };
  }
  if (pageId === 'detail') {
    // A base/create row (or a typeless row) is a valid detail target; an
    // edit/remove/model EVENT is not — folded away, it has no page of its own.
    const first = feed.entries.find((e) => {
      const t = e.payload?.type;
      return !t || !EVENT_TYPES.has(t);
    });
    return {
      item_id:
        first?.payload?.item_id ||
        first?.payload?.id ||
        first?.id ||
        first?.source_session_id ||
        null,
    };
  }
  return {};
}

/** Event rows — a correction or a delete is never a template for a new item. */
const EVENT_TYPES = new Set(['edit', 'remove', 'model']);

/**
 * ONE representative new row for the widget, derived from its own mock feed —
 * what the host would append after a capture, and what the studio's host capture button
 * writes in both mock and user mode.
 *
 * Identity is FRESHENED, not copied: the entry id, `payload.id`/`item_id` and
 * the name (medicine-box folds on the name) all move, or the fold would merge
 * the "new" row into its template and nothing would appear. Any date/time
 * field is stamped to now so the row lands in today's list.
 *
 * `imageUrl` replaces every image field with the photo the user actually
 * uploaded, so a row landed by the studio carries the real capture rather than
 * the template's stock picture.
 */
export function sampleAddition(widgetId, n = 1, { imageUrl = null } = {}) {
  const template = mockFeed(widgetId).entries.find(
    (e) => e.payload && !e.payload.edits && !EVENT_TYPES.has(e.payload.type),
  );
  if (!template) return null;

  const now = new Date();
  const ref = `studio-add-${n}`;
  const payload = { ...template.payload };
  // Freshen EVERY identity key on the payload — the fold key varies per widget
  // (id · item_id · piece_id · plant_id · …). Freshening only id/item_id let an
  // event-sourced widget (art/plant) fold the "new" row into its template (a
  // rename) instead of adding a row. A non-event base template's *_id fields are
  // its own identity, so this is safe.
  for (const k of Object.keys(payload)) {
    if (k === 'id' || k.endsWith('_id')) payload[k] = ref;
  }
  if (payload.name) payload.name = `New ${payload.name}${n > 1 ? ` ${n}` : ''}`;
  if (payload.date) payload.date = isoDay(now);
  if (payload.time) payload.time = now.toTimeString().slice(0, 5);
  if (imageUrl) {
    if ('image_url' in payload) payload.image_url = imageUrl;
    if ('photo_url' in payload) payload.photo_url = imageUrl;
    if (Array.isArray(payload.images)) payload.images = [imageUrl];
  }

  return {
    id: ref,
    kind: template.kind,
    payload,
    source_session_id: `${ref}-session`,
    log_card_id: `${ref}-card`,
    created_at: now.toISOString(),
  };
}
